import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  SuburbDataQuery,
  SuburbDataRow,
  SuburbDataService,
  SuburbFunnel,
} from '../../../../services/suburb-data.service';
import { apiErrorText } from '../workspace/api-errors';

/**
 * The Suburbs workspace's List view (#256; plan §6.2): every ABS suburb with its region, coverage, page and what the
 * crawl knows, to decide which suburbs to cover next. Paged, sorted and filtered on the server; the funnel follows
 * the same scope. A row opens the suburb's panel in the workspace. Crawl values are untrusted text: only ever
 * interpolated.
 *
 * Responses are handled inside NgZone explicitly: in this app an HTTP response can come back outside Angular's zone
 * (the token interceptor reads async storage), and then the table wouldn't redraw until the next click.
 */
@Component({
  selector: 'app-suburb-data-list',
  templateUrl: './suburb-data-list.component.html',
  styleUrls: ['./suburb-data-list.component.scss'],
})
export class SuburbDataListComponent implements OnInit, OnDestroy {
  /** Region id -> name, and the region filter's options (from the workspace). */
  @Input() regionNames = new Map<string, string>();
  @Input() regionOptions: Array<{ label: string; value: string }> = [];
  /** Sub-region id -> name, and the sub-regions grouped by region (#260). */
  @Input() subRegionNames = new Map<string, string>();
  @Input() set subRegionGroups(
    groups: Array<{ label: string; items: Array<{ label: string; value: string }> }>
  ) {
    this.subRegionFilter = [
      { label: 'Any', items: [{ label: 'In no sub-region', value: 'none' }] },
      ...(groups ?? []),
    ];
  }
  subRegionFilter: Array<{ label: string; items: Array<{ label: string; value: string }> }> = [];
  /** How many page records aren't a single suburb or a sub-region (places, untyped): shown beside the funnel. */
  @Input() otherPages = 0;
  @Output() open = new EventEmitter<string>();
  @Output() openOtherPages = new EventEmitter<void>();

  readonly pageSize = 50;
  readonly states = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT', 'OT'].map((s) => ({
    label: s,
    value: s,
  }));
  readonly statuses = [
    { label: 'Unassigned', value: 'unassigned' },
    { label: 'Assigned', value: 'assigned' },
    { label: 'Covered', value: 'covered' },
    { label: 'Candidates (assigned, not covered)', value: 'candidates' },
    { label: 'Has a page', value: 'page' },
  ];
  readonly buckets = ['rich', 'good', 'thin', 'bare', 'empty', 'none'].map((b) => ({
    label: b === 'none' ? 'no crawl data' : b,
    value: b,
  }));
  private readonly bucketOrder = ['rich', 'good', 'thin', 'bare', 'empty', 'none'];

  filters: SuburbDataQuery = {
    q: '',
    state: null,
    region: null,
    subRegion: null,
    status: null,
    bucket: null,
  };
  sort = 'name';
  dir: 'asc' | 'desc' = 'asc';
  first = 0;

  rows: SuburbDataRow[] = [];
  total = 0;
  loading = false;
  error = '';
  funnel: SuburbFunnel | null = null;

  private search$ = new Subject<void>();
  private subs: Subscription[] = [];
  private requestId = 0;

  constructor(
    private data: SuburbDataService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.subs.push(this.search$.pipe(debounceTime(300)).subscribe(() => this.reload()));
    this.reload();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  private inZone<T>(fn: (v: T) => void): (v: T) => void {
    return (v: T) => this.zone.run(() => fn(v));
  }

  regionName(id: string | null): string {
    return id ? (this.regionNames.get(id) ?? id) : '';
  }

  subRegionList(ids: string[] | undefined): string {
    return (ids ?? []).map((id) => this.subRegionNames.get(id) ?? id).join(', ');
  }

  /** Filters changed (or the map did): back to the first page, refresh the table and the funnel. */
  reload(): void {
    this.first = 0;
    this.loadPage();
    this.loadFunnel();
  }

  onSearch(): void {
    this.search$.next();
  }

  onLazyLoad(e: any): void {
    this.first = e.first ?? 0;
    if (e.sortField) {
      this.sort = e.sortField;
      this.dir = e.sortOrder === -1 ? 'desc' : 'asc';
    }
    this.loadPage();
  }

  private loadPage(): void {
    const id = ++this.requestId;
    this.loading = true;
    this.error = '';
    this.data
      .list({
        ...this.filters,
        sort: this.sort,
        dir: this.dir,
        offset: this.first,
        limit: this.pageSize,
      })
      .subscribe({
        next: this.inZone((r: { total: number; rows: SuburbDataRow[] }) => {
          if (id !== this.requestId) return; // a newer request is on its way
          this.rows = r.rows;
          this.total = r.total;
          this.loading = false;
        }),
        error: this.inZone((e: any) => {
          if (id !== this.requestId) return;
          this.loading = false;
          this.error = apiErrorText(e, 'could not load suburbs');
        }),
      });
  }

  private loadFunnel(): void {
    const { q, state, region } = this.filters;
    this.data.funnel({ q, state, region }).subscribe({
      next: this.inZone((f: SuburbFunnel) => (this.funnel = f)),
      error: this.inZone(() => (this.funnel = null)),
    });
  }

  /** Funnel shortcuts set the status (and bucket) filter. */
  showStatus(status: string | null, bucket: string | null = null): void {
    this.filters = { ...this.filters, status, bucket };
    this.reload();
  }

  resetFilters(): void {
    this.filters = {
      q: '',
      state: null,
      region: null,
      subRegion: null,
      status: null,
      bucket: null,
    };
    this.reload();
  }

  get anyFilter(): boolean {
    return Object.values(this.filters).some((v) => !!v);
  }

  bucketsOf(byBucket: Record<string, number> | undefined): Array<{ bucket: string; n: number }> {
    if (!byBucket) return [];
    return this.bucketOrder.filter((b) => byBucket[b]).map((b) => ({ bucket: b, n: byBucket[b] }));
  }
}
